import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { ICountry } from '../models/country';
import { countries } from '../data/countries';
import { TranslocoService } from '@jsverse/transloco';
import { User } from 'app/core/user/user.types';
import { DaysOfWeek } from '../models/common';

@Injectable({ providedIn: 'root' })
export class CommonService {
    // Private

    private _countries: BehaviorSubject<ICountry[] | null> =
        new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _translocoService: TranslocoService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for countries
     */
    get countries$(): Observable<ICountry[]> {
        return this._countries.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the avatar of the given person
     *
     * @param id
     * @param avatar
     */
    uploadAvatar(id: string, avatar: File): Observable<any> {
        return this._httpClient.post<any>(
            'api/apps/persons/avatar',
            {
                id,
                avatar,
            },
            {
                headers: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': avatar.type,
                },
            }
        );
    }

    /**
     * Get countries
     */
    getCountries(lang: string = 'de'): Observable<ICountry[]> {
        // The list is bundled with the app. Callers sort it in place, so hand out a copy;
        // unknown languages fall back to English.
        const list: ICountry[] = structuredClone(countries[lang] ?? countries.en);
        return of(list).pipe(
            tap((countryList) => {
                this._countries.next(countryList);
            })
        );
    }

    calculateAge(date) {
        const dob = new Date(date);
        //calculate month difference from current date in time
        const month_diff = Date.now() - dob.getTime();

        //convert the calculated difference in date format
        const age_dt = new Date(month_diff);

        //extract year from date
        const year = age_dt.getUTCFullYear();

        //now calculate the age of the user
        const age = Math.abs(year - 1970);
        console.log(age);
        return age;
    }

    weekDay(day) {
        return this._translocoService.getActiveLang() === 'de'
            ? this.t(day).substr(0, 2)
            : this.t(day).substr(0, 3);
    }

    dayKeyShort(day) {
        let key = '';
        switch (day) {
            case DaysOfWeek.MONDAY:
                key = 'mon';
                break;
            case DaysOfWeek.TUESDAY:
                key = 'tue';
                break;

            case DaysOfWeek.WEDNESDAY:
                key = 'wed';
                break;

            case DaysOfWeek.THURSDAY:
                key = 'thu';
                break;

            case DaysOfWeek.FRIDAY:
                key = 'fri';
                break;

            case DaysOfWeek.SATURDAY:
                key = 'sat';
                break;

            case DaysOfWeek.SUNDAY:
                key = 'sun';
                break;
        }
        return key;
    }

    boxDayShort(day: string) {
        let key = '';
        switch (day) {
            case DaysOfWeek.MONDAY:
                key = 'boxMonday';
                break;
            case DaysOfWeek.TUESDAY:
                key = 'boxTuesday';
                break;
            case DaysOfWeek.WEDNESDAY:
                key = 'boxWednesday';
                break;
            case DaysOfWeek.THURSDAY:
                key = 'boxThursday';
                break;
            case DaysOfWeek.FRIDAY:
                key = 'boxFriday';
                break;
            case DaysOfWeek.SATURDAY:
                key = 'boxSaturday';
                break;
            case DaysOfWeek.SUNDAY:
                key = 'boxSunday';
                break;
        }
        return key;
    }

    getAvatar(user: User) {
        if (!user) {
            return 'N';
        }
        return user.firstName
            ? user.firstName.charAt(0)
            : user.nickName
            ? user.nickName.charAt(0)
            : 'N';
    }

    getName(user: User) {
        if (!user.firstName && !user.lastName) {
            return user.nickName;
        }
        return user.firstName + ' ' + user.lastName;
    }

    t(key: string) {
        return this._translocoService.translate(key);
    }
}
